import { useMemo, useState, type ReactNode } from "react";
import { useCustom, useGetLocale, useOne, useTranslate } from "@refinedev/core";
import {
  AlertTriangle,
  Ban,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Euro,
  FileText,
  History,
  Mail,
  MapPin,
  PawPrint,
  Pencil,
  Phone,
  Plus,
  ReceiptText,
  Sun,
  UserRound,
} from "lucide-react";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import { useWerkmodus } from "../components/werkmodus";
import { formatEuro, todayIso } from "../i18n/format";
import { volledigeNaam } from "./RelatieDetail";
import { useRelatieTegelbord } from "./RelatieTegelbord";
import { GeschiedenisTab, type RelatieDetail as RelatieDetailData } from "./relatieTiles";
import { FacturenTab, useKlantFacturen } from "./pensionklant/FacturenTab";
import { DierCreateDialog } from "./DierCreateDialog";
import { DierDetail } from "./pensionklant/DierDetail";
import { DierenLijst } from "./pensionklant/DierenLijst";
import { LijstInstellen } from "./pensionklant/Instellen";
import { useDieroverzichtVoorkeur } from "./pensionklant/useDieroverzichtVoorkeur";
import { ReserveringenTab } from "./pensionklant/BoekingTabs";
import { DagopvangTab } from "./pensionklant/DagopvangTab";
import { PensionTab } from "./pensionklant/ReserveringTab";
import { NotitiesTab } from "./pensionklant/NotitiesTab";
import { NieuweReserveringWizard } from "./pensionreservering/NieuweReserveringWizard";
import { Sectie } from "./pensionklant/Sectie";
import type { Context } from "./pensionklant/hulp";
import type { Noodcontact, PensionKlantData } from "./pensionKlantTypes";
import "./PensionKlant.css";

export interface PensionKlantProps {
  id: string;
  onBack: () => void;
  onOpenFactuur?: (factuurId: string) => void;
  /** Een factuur beginnen voor deze klant -- een leeg scherm, nog geen factuur. */
  onNieuweFactuur?: (klantId: string) => void;
  /** Opent het dierdossier; het id is een string omdat het relatiescherm dat ook zo doorgeeft. */
  onOpenDier?: (dierId: string) => void;
  /** Opent de kennelplanning, vanuit het plaatsingsvoorstel bij een dier. */
  onOpenPlanning?: () => void;
  /** Opent een andere klant — na het aanmaken van een nieuwe. */
  onOpenKlant?: (klantId: string) => void;
  /** Het dier dat bij het openen gekozen staat (vanuit de pensionoverzichten); anders het eerste. */
  startDierId?: number | null;
}

type Tab =
  | "relatie"
  | "dieren"
  | "pension"
  | "dagopvang"
  | "reserveringen"
  | "facturen"
  | "notities"
  | "geschiedenis";

const TABS: { sleutel: Tab; icoon: ReactNode }[] = [
  { sleutel: "relatie", icoon: <UserRound size={19} strokeWidth={1.8} /> },
  { sleutel: "dieren", icoon: <PawPrint size={19} strokeWidth={1.8} /> },
  { sleutel: "pension", icoon: <CalendarDays size={19} strokeWidth={1.8} /> },
  { sleutel: "dagopvang", icoon: <Sun size={19} strokeWidth={1.8} /> },
  { sleutel: "reserveringen", icoon: <CalendarDays size={19} strokeWidth={1.8} /> },
  { sleutel: "facturen", icoon: <ReceiptText size={19} strokeWidth={1.8} /> },
  { sleutel: "notities", icoon: <FileText size={19} strokeWidth={1.8} /> },
  { sleutel: "geschiedenis", icoon: <History size={19} strokeWidth={1.8} /> },
];

/**
 * Het klantscherm van de werkmodus Pension.
 *
 * <p>Een pension kijkt naar een klant met andere ogen dan een asiel: het wil zijn eigen huisdieren
 * zien, wat daarvoor geboekt staat, en wat er bij het brengen moet gebeuren. Bovenaan de klant met zijn
 * getallen, daaronder tabbladen. Op "Dieren" staat links de lijst en rechts het gekozen dier, met
 * tegels die elke medewerker zelf inricht.
 *
 * <p>De dieren komen uit {@code dier.klant_id} (eigendom), niet uit een verblijf. Naam en
 * contactgegevens komen uit {@code GET /api/relaties/{id}}; het pensiondeel uit
 * {@code GET /api/pension/klanten/{id}}. Het tabblad Relatie toont het tegelbord van het relatiescherm,
 * met dezelfde bewaarde indeling; "Bewerken" gaat daarheen.
 */
export function PensionKlant({ id, onOpenFactuur, onNieuweFactuur, onOpenDier, onOpenPlanning, startDierId }: PensionKlantProps) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [tab, setTab] = useState<Tab>("dieren");
  const [gekozen, setGekozen] = useState<number | null>(startDierId ?? null);
  const [nieuwDier, setNieuwDier] = useState(false);
  // De velden op een dierkaartje en de tegels van het dieroverzicht zijn één voorkeur, dus die staat hier.
  const [veldenInstellen, setVeldenInstellen] = useState(false);
  const { voorkeur, opslaan: bewaarVoorkeur } = useDieroverzichtVoorkeur();
  const [nieuweReservering, setNieuweReservering] = useState(false);

  // useOne en niet useCustom: de tegel Relatiegegevens bewaart via useUpdate op dezelfde resource, en
  // dan ververst de kop hier vanzelf mee.
  const { data: relatieData } = useOne<RelatieDetailData>({ resource: "relaties", id });
  const { data: pensionData, isLoading, isError, refetch } = useCustom<PensionKlantData>({
    url: `pension/klanten/${id}`,
    method: "get",
  });
  // Alle facturen, niet de nieuwste vijftig van de relatietegel: anders klopt het openstaande bedrag
  // bij een vaste klant niet. Refine deelt deze aanroep met het tabblad Facturen.
  const { data: facturenData } = useKlantFacturen(id);

  const relatie = relatieData?.data;
  const pension = pensionData?.data;
  const facturen = facturenData?.data;
  const dieren = pension?.dieren ?? [];
  const fotos = useAnimalPhotoUrls(dieren.map((d) => d.id));
  // Wat de klant nog moet betalen: ook wat al over de vervaldatum is, anders lijkt een wanbetaler een goede klant.
  const openstaand = (facturen?.totaalOpenstaand ?? 0) + (facturen?.totaalVervallen ?? 0);
  const ctx: Context = useMemo(() => ({ t, locale, vandaag: todayIso() }), [t, locale]);

  const reserveringen = pension?.reserveringen ?? [];
  // Vanaf elk tabblad naar een dier: kiezen en naar het tabblad Dieren.
  const kiesDier = (dierId: number) => {
    setGekozen(dierId);
    setTab("dieren");
  };

  // Zonder Facturen: die hebben hier hun eigen tabblad. Verblijven heet hier Asielverblijven, want op
  // dit scherm gaat "verblijf" anders over het pension.
  // Een asielverblijf openen is asielwerk: de werkmodus schakelt mee naar Asiel, zodat het dierscherm
  // en het menu bij het verblijf passen. Alleen voor het relatiebord — daar opent alleen de tegel
  // Asielverblijven een dier; de eigen dieren van de klant blijven in Pension.
  const [, setWerkmodus] = useWerkmodus();
  const openAsielverblijf = onOpenDier
    ? (dierId: string) => {
        setWerkmodus("asiel");
        onOpenDier(dierId);
      }
    : undefined;
  const relatieBord = useRelatieTegelbord({
    id,
    detail: relatie,
    onOpenFactuur,
    onNieuweFactuur,
    onOpenDier: openAsielverblijf,
    zonder: ["facturen"],
    titels: { verblijven: t("pensionKlant.relatie.asielverblijven") },
  });

  // Zonder keuze het eerste dier: een leeg rechterdeel zegt niets, en bij één dier is kiezen onzin.
  const dier = dieren.find((d) => d.id === gekozen) ?? dieren[0] ?? null;

  return (
    <div className="pk">
      <section className="pk-kop" aria-label={t("pensionKlant.kop.aria")}>
        <div className="pk-kop__titel">
          {/* De naam zoals je hem uitspreekt: "Fam. M. van Diessen". Zelfde helper als het
              relatiescherm, zodat de twee schermen dezelfde naam tonen. */}
          <h1>{relatie ? volledigeNaam(relatie) : t("common.loading")}</h1>
          {pension && (
            <span className="pk-chip" data-toon={pension.actief ? "groen" : "grijs"}>
              {pension.actief ? <CheckCircle2 size={15} aria-hidden="true" /> : <Ban size={15} aria-hidden="true" />}
              {pension.actief ? t("pensionKlant.chip.actief") : t("pensionKlant.chip.inactief")}
            </span>
          )}
          {(pension?.kpis.dierenMetWaarschuwing ?? 0) > 0 && (
            <span
              className="pk-chip"
              data-toon="amber"
              title={t(
                pension!.kpis.dierenMetWaarschuwing === 1
                  ? "pensionKlant.melding.waarschuwing.een"
                  : "pensionKlant.melding.waarschuwing.meer",
                { args: [pension!.kpis.dierenMetWaarschuwing] },
              )}
            >
              <AlertTriangle size={15} aria-hidden="true" />
              {t("pensionKlant.chip.bijzonderheden")}
            </span>
          )}
          <button type="button" className="pk-knop pk-knop--hoofd pk-kop__nieuw" onClick={() => setNieuweReservering(true)}>
            <Plus size={16} aria-hidden="true" />
            {t("nieuweReservering.knop")}
          </button>
        </div>

        <div className="pk-kop__inhoud">
          <div className="pk-contact">
            <Contact icoon={<MapPin size={19} />} tint="grijs">
              {adresRegels(relatie).map((regel) => (
                <span key={regel}>{regel}</span>
              ))}
            </Contact>
            <Contact icoon={<Mail size={19} />} tint="accent" label={t("pensionKlant.contact.email")}>
              {relatie?.emailAdres ? <a href={`mailto:${relatie.emailAdres}`}>{relatie.emailAdres}</a> : <span>—</span>}
            </Contact>
            <Contact icoon={<Phone size={19} />} tint="groen">
              {[relatie?.telefoon1, relatie?.telefoon2].filter(Boolean).length === 0 ? (
                <span>—</span>
              ) : (
                [relatie?.telefoon1, relatie?.telefoon2].filter(Boolean).map((nr) => <span key={nr as string}>{nr}</span>)
              )}
            </Contact>
            {/* Wie je belt als de eigenaar op vakantie is — daarom staat het er ook als het leeg is. */}
            <Contact icoon={<UserRound size={19} />} tint="rood" label={t("pensionKlant.contact.noodcontact")}>
              <strong>{noodcontactRegel(pension?.noodcontact, t)}</strong>
            </Contact>
            <button type="button" className="pk-knop pk-kop__bewerken" onClick={() => setTab("relatie")}>
              <Pencil size={15} aria-hidden="true" />
              {t("pensionKlant.actie.bewerken")}
            </button>
          </div>

          <div className="pk-getallen">
            <Getal icoon={<PawPrint size={19} />} tint="groen" waarde={pension?.kpis.dieren} label={t("pensionKlant.kpi.dieren")} />
            <Getal
              icoon={<CalendarDays size={19} />}
              tint="grijs"
              waarde={pension ? (pension.kpis.pensionBoekingen ?? pension.kpis.reserveringen) : undefined}
              label={t("pensionKlant.kpi.pensionBoekingen")}
            />
            <Getal
              icoon={<Sun size={19} />}
              tint="geel"
              waarde={pension ? (pension.kpis.dagopvangBoekingen ?? 0) : undefined}
              label={t("pensionKlant.kpi.dagopvangBoekingen")}
            />
            <Getal
              icoon={<Euro size={19} />}
              tint="rood"
              waarde={facturen ? formatEuro(openstaand, locale) : undefined}
              label={t("pensionKlant.kpi.openstaand")}
            />
            {openstaand > 0 && (
              <button type="button" className="pk-getallen__link" onClick={() => setTab("facturen")}>
                {t("pensionKlant.actie.naarFacturen")}
                <ChevronRight size={15} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </section>

      <nav className="pk-tabs" aria-label={t("pensionKlant.tabs.aria")}>
        <ul role="tablist">
          {TABS.map((tb) => (
            <li key={tb.sleutel} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={tab === tb.sleutel}
                className="pk-tab"
                onClick={() => setTab(tb.sleutel)}
              >
                <span aria-hidden="true">{tb.icoon}</span>
                {t(`pensionKlant.tab.${tb.sleutel}`)}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {tab === "dieren" && (
        <div className="pk-dieren" role="tabpanel">
          <DierenLijst
            dieren={dieren}
            gekozen={dier?.id ?? null}
            fotos={fotos}
            laden={isLoading}
            fout={isError}
            onOpnieuw={() => refetch()}
            onKies={setGekozen}
            onNieuwDier={() => setNieuwDier(true)}
            voorkeur={voorkeur}
            onVelden={() => setVeldenInstellen(true)}
            ctx={ctx}
          />
          {dier ? (
            <DierDetail
              key={dier.id}
              dier={dier}
              foto={fotos[String(dier.id)]}
              onNaarPension={() => setTab("pension")}
              ctx={ctx}
              onGewijzigd={() => refetch()}
            />
          ) : (
            <div className="pk-paneel pk-paneel--leeg">
              <p>{isLoading ? t("common.loading") : t("pensionKlant.dieren.kiesEen")}</p>
            </div>
          )}
        </div>
      )}
      {tab !== "dieren" && (
        <div role="tabpanel" className="pk-bladvak" aria-label={t(`pensionKlant.tab.${tab}`)}>
          {tab === "relatie" && (
            <div className="pk-relatie fawkes-relatie-detail">
              <div className="pk-relatie__balk">{relatieBord.knoppen}</div>
              {relatieBord.bord ?? <p className="pk-tegel__leeg">{t("common.loading")}</p>}
              {relatieBord.vensters}
            </div>
          )}
          {tab === "pension" && (
            <PensionTab
              reserveringen={reserveringen}
              dieren={dieren}
              fotos={fotos}
              laden={isLoading}
              ctx={ctx}
              onKiesDier={kiesDier}
              onOpenFactuur={onOpenFactuur}
              onGewijzigd={() => refetch()}
              klantNaam={relatie ? volledigeNaam(relatie) : null}
              klantId={Number(id)}
              onOpenPlanning={onOpenPlanning}
            />
          )}
          {tab === "dagopvang" && (
            <DagopvangTab klantId={id} dieren={dieren} fotos={fotos} ctx={ctx} onKiesDier={kiesDier} />
          )}
          {tab === "reserveringen" && (
            <ReserveringenTab
              klantId={id}
              reserveringen={reserveringen}
              laden={isLoading}
              ctx={ctx}
              onKiesDier={kiesDier}
            />
          )}
          {tab === "facturen" && (
            <FacturenTab klantId={id} ctx={ctx} onOpenFactuur={onOpenFactuur}
              onNieuweFactuur={onNieuweFactuur} />
          )}
          {tab === "notities" && <NotitiesTab klantId={id} ctx={ctx} />}
          {tab === "geschiedenis" && (
            <Sectie titel={t("pensionKlant.tab.geschiedenis")} icoon={History} tint="grijs">
              <GeschiedenisTab relatieId={id} />
            </Sectie>
          )}
        </div>
      )}

      <LijstInstellen
        open={veldenInstellen}
        voorkeur={voorkeur}
        ctx={ctx}
        onClose={() => setVeldenInstellen(false)}
        onOpslaan={bewaarVoorkeur}
      />

      {nieuweReservering && (
        <NieuweReserveringWizard
          klant={{
            id: Number(id),
            naam: relatie ? volledigeNaam(relatie) : "",
            adres:
              [
                [relatie?.straat, relatie?.huisnummer, relatie?.huisnummerToevoeging].filter(Boolean).join(" "),
                [relatie?.postcode, relatie?.woonplaats].filter(Boolean).join(" "),
              ]
                .filter((regel) => regel.trim().length > 0)
                .join(", ") || null,
            email: relatie?.emailAdres ?? null,
            telefoon: relatie?.telefoon1 ?? relatie?.telefoon2 ?? null,
          }}
          onSluit={() => {
            setNieuweReservering(false);
            refetch();
          }}
        />
      )}
      {nieuwDier && (
        <DierCreateDialog
          klantId={Number(id)}
          onClose={() => setNieuwDier(false)}
          onGemaakt={(dierId) => {
            setNieuwDier(false);
            // Het nieuwe dier hoort meteen op de lijst te staan, en gekozen te zijn.
            refetch();
            setGekozen(dierId);
          }}
        />
      )}
    </div>
  );
}

function Contact({
  icoon,
  tint,
  label,
  children,
}: {
  icoon: ReactNode;
  /** De tint van het rondje; elk veld zijn eigen kleur, zoals in het ontwerp. */
  tint: "grijs" | "accent" | "groen" | "rood";
  label?: string;
  children: ReactNode;
}) {
  return (
    <div className="pk-contact__vak">
      <span className="pk-rondje" data-tint={tint} aria-hidden="true">
        {icoon}
      </span>
      <div className="pk-contact__tekst">
        {label && <span className="pk-contact__label">{label}</span>}
        {children}
      </div>
    </div>
  );
}

function Getal({
  icoon,
  tint,
  waarde,
  label,
}: {
  icoon: ReactNode;
  tint: "groen" | "grijs" | "geel" | "rood";
  waarde: string | number | undefined;
  label: string;
}) {
  return (
    <div className="pk-getal">
      <span className="pk-rondje" data-tint={tint} aria-hidden="true">
        {icoon}
      </span>
      <span className="pk-getal__tekst">
        <span className="pk-getal__waarde">{waarde ?? "—"}</span>
        <span className="pk-getal__label">{label}</span>
      </span>
    </div>
  );
}

/** Naam en nummer op één regel, gescheiden door een kastlijntje. */
function noodcontactRegel(noodcontact: Noodcontact | null | undefined, t: (key: string) => string): string {
  if (!noodcontact) return t("pensionKlant.contact.geenNoodcontact");
  return [noodcontact.naam, noodcontact.telefoon].filter(Boolean).join(" — ");
}

function adresRegels(relatie: RelatieDetailData | undefined): string[] {
  if (!relatie) return ["—"];
  const straat = [relatie.straat, relatie.huisnummer, relatie.huisnummerToevoeging].filter(Boolean).join(" ");
  const plaats = [relatie.postcode, relatie.woonplaats].filter(Boolean).join(" ");
  const regels = [straat, plaats, relatie.land ?? ""].filter((r) => r.trim().length > 0);
  return regels.length > 0 ? regels : ["—"];
}
